/**
 * @license Copyright (c) 2014-2023, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
// クイックビルドコマンド：cd /src/frontend/app/ckeditor && npm run build && cd ../ && npm start
// 箇条書き/番号付きリストは次のファイルを直接編集する
// \node_modules\@ckeditor\ckeditor5-list\src\documentlistproperties\documentlistpropertiesediting.js
import CKFinderUploadAdapter from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter.js';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment.js';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat.js';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold.js';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote.js';
import CKFinder from '@ckeditor/ckeditor5-ckfinder/src/ckfinder.js';
import Comments from '@ckeditor/ckeditor5-comments/src/comments.js';
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor.js';
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials.js';
import FontColor from '@ckeditor/ckeditor5-font/src/fontcolor.js';
// import DocumentList from '@ckeditor/ckeditor5-list/src/documentlist.js';
// import DocumentListProperties from '@ckeditor/ckeditor5-list/src/documentlistproperties.js';

import Heading from '@ckeditor/ckeditor5-heading/src/heading.js';
import GeneralHtmlSupport from '@ckeditor/ckeditor5-html-support/src/generalhtmlsupport.js';
import CustomHtmlSupport from './customHtmlSupport.js';

import { Image, ImageCaption, ImageStyle, ImageToolbar, ImageUpload } from '@ckeditor/ckeditor5-image';

import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic.js';
import Subscript from '@ckeditor/ckeditor5-basic-styles/src/subscript.js';
import Superscript from '@ckeditor/ckeditor5-basic-styles/src/superscript.js';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline.js';
import Link from '@ckeditor/ckeditor5-link/src/link.js';
import MediaEmbed from '@ckeditor/ckeditor5-media-embed/src/mediaembed.js';
import PageBreak from '@ckeditor/ckeditor5-page-break/src/pagebreak.js';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph.js';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice.js';
import RemoveFormat from '@ckeditor/ckeditor5-remove-format/src/removeformat.js';
import RevisionHistory from '@ckeditor/ckeditor5-revision-history/src/revisionhistory.js';
import { SourceEditing } from '@ckeditor/ckeditor5-source-editing';
import SpecialCharacters from '@ckeditor/ckeditor5-special-characters/src/specialcharacters.js';
import SpecialCharactersCurrency from '@ckeditor/ckeditor5-special-characters/src/specialcharacterscurrency.js';
import SpecialCharactersText from '@ckeditor/ckeditor5-special-characters/src/specialcharacterstext.js';
import Style from '@ckeditor/ckeditor5-style/src/style.js';
import Table from '@ckeditor/ckeditor5-table/src/table.js';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar.js';
import Template from '@ckeditor/ckeditor5-template/src/template.js';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation.js';
import { toMap, uid } from '@ckeditor/ckeditor5-utils';

// import { CustomButtonAddId } from './customButtonAddId.js';
import { customButtonCommentSearch } from './customButtonCommentSearch.js';
import { CustomButtonDecisionText } from './customButtonDecisionText.js';
import { customButtonOriSearch } from './customButtonOriSearch.js';
import { CustomButtonPsCopy } from './customButtonPsCopy.js';
import { CustomButtonPsInfo } from './customButtonPsInfo.js';
import { customButtonPsNote } from './customButtonPsNote.js';
import { CustomButtonPsPaste } from './customButtonPsPaste.js';
import { CustomButtonStructureTree } from './customButtonStructureTree.js';
import { CustomCheckBox } from './customCheckBox.js';
import { CustomCheckBoxReqSpec } from './customCheckBoxReqSpec.js';
import { CustomCommentButton } from './customCommentButton.js';
import { CustomImageChoosingButton } from './customImageChoosingButton.js';
import { CustomImageReplacingButton } from './customImageReplacingButton.js';
import { CustomTableLayout } from './customTableLayout.js';
import { HeadingAttribute } from './headingAttribute.js';
import MyCommentThreadView from './myCommentThreadView';
import MyCommentView from './myCommentview';
import { SimpleBox } from './simplebox.js';

import inlineIcon from '../node_modules/@ckeditor/ckeditor5-core/theme/icons/object-inline.svg';

import fullColumnImageWithBorder from './full_column_image_with_border.svg';
import fullColumnImageWithoutBorder from './full_column_image_without_border.svg';
import imageWithBorder from './image_with_border.svg';
import imageWithoutBorder from './image_without_border.svg';

// Localization
import i18n from '../../src/i18n/index.ts';

// Custom Italic Button
// import { CustomItalicButton } from './customItalicButton.js';

// Custom List
import { CustomOLButton } from './customOLButton.js';
import { CustomOLTextStart } from './customOLTextStart.js';
import { CustomULButton } from './customULButton.js';
import customListItems from './data/customListItems.js';

// リンク更新ボタン
import { CustomButtonReference } from './customButtonReference.js';

// Just for development
window.dd = console.log;

function SpecialCharactersCircles(editor) {
  editor.plugins.get('SpecialCharacters').addItems(
    'Circles',
    [
      { title: 'circle one', character: '①' },
      { title: 'circle two', character: '②' },
      { title: 'circle three', character: '③' },
      { title: 'circle four', character: '④' },
      { title: 'circle five', character: '⑤' },
      { title: 'circle six', character: '⑥' },
      { title: 'circle seven', character: '⑦' },
      { title: 'circle eight', character: '⑧' },
      { title: 'circle nine', character: '⑨' },
      { title: 'circle ten', character: '⑩' },
      { title: 'circle eleven', character: '⑪' },
      { title: 'circle twelve', character: '⑫' },
      { title: 'circle thirteen', character: '⑬' },
      { title: 'circle fourteen', character: '⑭' },
      { title: 'circle fifteen', character: '⑮' },
      { title: 'circle sixteen', character: '⑯' },
      { title: 'circle seventeen', character: '⑰' },
      { title: 'circle eighteen', character: '⑱' },
      { title: 'circle nineteen', character: '⑲' },
      { title: 'circle twenty', character: '⑳' },
      { title: 'circle twenty one', character: '㉑' },
      { title: 'circle twenty two', character: '㉒' },
      { title: 'circle twenty three', character: '㉓' },
      { title: 'circle twenty four', character: '㉔' },
      { title: 'circle twenty five', character: '㉕' },
      { title: 'circle twenty six', character: '㉖' },
    ],
    { label: editor.i18n.t('丸数字') }
  );
  editor.plugins.get('SpecialCharacters').addItems(
    'Mathematical Operators',
    [
      { title: 'Plus Sign', character: '\u002B' },
      { title: 'Equals Sign', character: '\u003D' },
      { title: 'Plus-Minus Sign', character: '\u00B1' },
      { title: 'Multiplication Sign', character: '\u00D7' },
      { title: 'Minus Sign', character: '\u2212' },
      { title: 'En Dash', character: '\u2013' },
      { title: 'Em Dash', character: '\u2014' },
      { title: 'Degree Sign', character: '\u00B0' },
      { title: 'Less-Than Sign', character: '\u003C' },
      { title: 'Greater-Than Sign', character: '\u003E' },
      { title: 'Less-Than or Equal To', character: '\u2264' },
      { title: 'Greater-Than or Equal To', character: '\u2265' },
      { title: 'Less-Than Over Equal To', character: '\u2266' },
      { title: 'Greater-Than Over Equal To', character: '\u2267' },
    ],
    { label: editor.i18n.t('数学記号') }
  );
  editor.plugins.get('SpecialCharacters').addItems(
    'Punction Marks',
    [
      { title: 'Left Double Quotation Mark', character: '\u201C' },
      { title: 'Right Double Quotation Mark', character: '\u201D' },
      { title: 'Left Single Quotation Mark', character: '\u2018' },
      { title: 'Right Single Quotation Mark', character: '\u2019' },
      { title: 'Bullet', character: '\u2022' },
      { title: 'Middle Dot', character: '\u00B7' },
    ],
    { label: editor.i18n.t('引用符など') }
  );
  editor.plugins.get('SpecialCharacters').addItems(
    'Arrows',
    [
      { title: 'Heavy Wide-Headed Rightwards Arrow', character: '\u2794' },
      // { title: 'Rightwards Arrow', character: '\u2192' },
      // { title: 'Rightwards White Arrow', character: '\u21E8' },
      // { title: 'Black Right-Pointing Double Triangle', character: '\u23E9' },
      // { title: 'Black Right-Pointing Small Triangle', character: '\u23F5' },
      // { title: 'Heavy Wide-Headed Rightwards Arrow', character: '\u2794' },
      // { title: 'Right Arrow With Small Equilateral Arrowhead', character: '\u1F86A' },
      // { title: 'Right Arrow With Medium Equilateral Arrowhead', character: '\u1F872' },
      // { title: 'Black Rightwards Arrowhead', character: '\u27AE' },
      // { title: 'Heavy Rightwards Arrow', character: '\u279C' },
    ],
    { label: editor.i18n.t('矢印') }
  );
}

function InitLocalization(editor) {
  // editor.i18n で多言語オブジェクトを取得できるようにする
  // 翻訳データは react-i18next と共通化しています。
  // そのため、もし追加したい場合は、frontend/app/src/i18n/en.json などへセット後「npm run build」でこのファイルをビルドしてください。
  editor.i18n = i18n;
  editor.commentCategories = getCommentCategories();
}
function getCommentCategories() {
  const { t } = i18n;
  const colors = {
    pending: '#FD2828',
    labQuestion: '#118919',
    hhsToOriginal: '#2222FD',
    originalToHhs: '#881188',
    hmValidation: '#D2507E',
    originalValidation: '#00B2ED',
  };
  return [
    {
      id: 1,
      key: 'pending',
      withText: true,
      label: t('懸案項目'),
      color: colors.pending,
      labelStyle: `color: ${colors.pending}`,
      commandName: 'addCommentThread',
      commandParam: { threadId: '1-abc' },
    },
    {
      id: 2,
      key: 'labQuestion',
      withText: true,
      label: t('研究所質問'),
      color: colors.labQuestion,
      labelStyle: `color: ${colors.labQuestion}`,
      commandName: 'addCommentThread',
      commandParam: { threadId: '2-3e215af890edf' },
    },
    {
      id: 3,
      key: 'hhsToOriginal',
      withText: true,
      label: t('Prod.⇒原作'),
      color: colors.hhsToOriginal,
      labelStyle: `color: ${colors.hhsToOriginal}`,
      commandName: 'addCommentThread',
      commandParam: { threadId: '3-3e215af890edf' },
    },
    {
      id: 4,
      key: 'originalToHhs',
      withText: true,
      label: t('原作⇒Prod.'),
      color: colors.originalToHhs,
      labelStyle: `color: ${colors.originalToHhs}`,
      commandName: 'addCommentThread',
      commandParam: { threadId: '4-3e215af890edf' },
    },
    // 以下Validation用
    {
      id: 8,
      key: 'hmValidation',
      withText: true,
      label: t('Validation HM用'),
      color: colors.hmValidation,
      labelStyle: `color: ${colors.hmValidation}`,
      commandName: 'addCommentThread',
      commandParam: { threadId: '8-3e215af890edf' },
    },
    {
      id: 9,
      key: 'originalValidation',
      withText: true,
      label: t('Validation 原作用'),
      color: colors.originalValidation,
      labelStyle: `color: ${colors.originalValidation}`,
      commandName: 'addCommentThread',
      commandParam: { threadId: '9-3e215af890edf' },
    },
  ];
}

function InitUtils(editor) {
  // editor.utils でユーティリティ関数を取得できるようにする
  // 不要なものは含めるべきでないので、最小限のもののみをセットしてください。
  editor.utils = {
    uid,
    toMap,
    getNewThreadId(prefix) {
      // ckeditor/src/customCommentButton.js で使用していた関数をこちらへ移動
      // 数値が2桁以下の場合は0埋めした方がいいと思いましたが、修正するとエラーになる可能性があるため、そのままにしています。
      const d = new Date();
      const dateString =
        String(d.getFullYear()) +
        String(d.getMonth() + 1) +
        String(d.getDate()) +
        String(d.getHours()) +
        String(d.getMinutes()) +
        String(d.getSeconds()) +
        String(d.getMilliseconds());
      return prefix + '-' + dateString;
    },
  };
}

function InitCustomList(editor) {
  // <ul> と <ol> のカスタマイズ
  const items = customListItems; // /src/frontend/app/ckeditor/src/data/customListItems.js で一元管理しています
  const classNames = items.map((item) => item.className);
  const getUlItems = (orderBy = 'asc') => {
    const ulItems = items.filter((item) => item.type === 'ul');
    const sortedUlItems = _.sortBy(ulItems, 'order');

    return orderBy === 'asc' ? sortedUlItems : sortedUlItems.reverse();
  };
  const getOlItems = (orderBy = 'asc') => {
    const olItems = items.filter((item) => item.type === 'ol');
    const sortedOlItems = _.sortBy(olItems, 'order');

    return orderBy === 'asc' ? sortedOlItems : sortedOlItems.reverse();
  };
  const getAllClassNames = () => {
    return items.map((item) => item.className);
  };
  const getUlClassNames = () => {
    return getUlItems().map((item) => item.className);
  };
  const getOlClassNames = () => {
    return getOlItems().map((item) => item.className);
  };
  const canSetNumber = (classNames) => {
    // 数値をセットできる要素かどうかチェック
    if (!classNames instanceof Array) classNames = [classNames];
    const numberClassNames = items
      .filter((item) => {
        return item.canSetNumber === true;
      })
      .map((item) => item.className);
    for (let i = 0; i < classNames.length; i++) {
      const className = classNames[i];
      if (numberClassNames.includes(className)) {
        return true;
      }
    }
    return false;
  };
  editor.customList = {
    items,
    classNames,
    canSetNumber,
    getUlItems,
    getOlItems,
    getAllClassNames,
    getUlClassNames,
    getOlClassNames,
  };
}

// Plugins to include in the build.
const sectionBuiltinPlugins = [
  InitLocalization,
  InitUtils,
  Alignment,
  Autoformat,
  BlockQuote,
  Bold,
  CKFinder,
  CKFinderUploadAdapter,
  Comments,
  // DocumentList,
  // DocumentListProperties,
  Essentials,
  FontColor,
  // FontFamily,
  // FontSize,
  GeneralHtmlSupport,
  CustomHtmlSupport,
  Heading,
  Image,
  ImageCaption,
  // ImageResizeEditing,
  // ImageResizeHandles,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Italic,
  // CustomItalicButton,
  Link,
  MediaEmbed,
  PageBreak,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  RevisionHistory,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersCurrency,
  SpecialCharactersText,
  SpecialCharactersCircles,
  InitCustomList,
  Subscript,
  Superscript,
  Style,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
  Template,
  TextTransformation,
  Underline,
  CustomCommentButton,
  CustomCheckBox,
  CustomButtonDecisionText,
  // CustomButtonBlock,
  // CustomButtonLBlock,
  CustomCheckBoxReqSpec,
  CustomULButton,
  CustomOLButton,
  CustomOLTextStart,
  // CustomButtonAddId,
  // TemplateParts,
  // AnyClass,
  SimpleBox,
  // CustomButtonAddFinalParagraph,
  HeadingAttribute,
  CustomImageChoosingButton,
  CustomImageReplacingButton,
  CustomButtonStructureTree,
  CustomButtonPsCopy,
  CustomButtonPsPaste,
  CustomButtonPsInfo,
  customButtonPsNote,
  customButtonOriSearch,
  customButtonCommentSearch,
  CustomTableLayout,
  CustomButtonReference,
];

// Plugins to include in the build.
const psBuiltinPlugins = [
  InitLocalization,
  InitUtils,
  Alignment,
  Autoformat,
  BlockQuote,
  Bold,
  CKFinder,
  CKFinderUploadAdapter,
  Comments,
  // DocumentList,
  // DocumentListProperties,
  Essentials,
  FontColor,
  // FontFamily,
  // FontSize,
  GeneralHtmlSupport,
  CustomHtmlSupport,
  Heading,
  Image,
  ImageCaption,
  // ImageResizeEditing,
  // ImageResizeHandles,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Italic,
  // CustomItalicButton,
  Link,
  MediaEmbed,
  PageBreak,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  RevisionHistory,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersCurrency,
  SpecialCharactersText,
  SpecialCharactersCircles,
  InitCustomList,
  Subscript,
  Superscript,
  Style,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
  Template,
  TextTransformation,
  Underline,
  CustomCommentButton,
  CustomCheckBox,
  // CustomButtonDecisionText,
  // CustomButtonBlock,
  // CustomButtonLBlock,
  CustomCheckBoxReqSpec,
  CustomULButton,
  CustomOLButton,
  CustomOLTextStart,
  // CustomButtonAddId,
  // TemplateParts,
  // AnyClass,
  SimpleBox,
  // CustomButtonAddFinalParagraph,
  HeadingAttribute,
  CustomImageChoosingButton,
  CustomImageReplacingButton,
  CustomButtonStructureTree,
  CustomButtonPsCopy,
  CustomButtonPsPaste,
  CustomButtonPsInfo,
  customButtonPsNote,
  customButtonOriSearch,
  customButtonCommentSearch,
  CustomTableLayout,
];

// Editor configuration.
const baseConfig = {
  language: i18n.language,
  toolbar: {
    items: [
      'bold',
      'italic',
      'underline',
      // 'fontColor',
      // 'fontFamily',
      // 'fontSize',
      'superscript',
      'subscript',
      'specialCharacters',
      'link',
      // 'customItalicButton',
      // 'bulletedList',
      // 'numberedList',
      'customULButton',
      'customOLButton',
      'customOLTextStart',
      '|',
      // 'outdent',
      // 'indent',
      'alignment',
      'pageBreak',
      '|',
      'blockQuote',
      'insertTable',
      'undo',
      'redo',
      '|',
      'customButtonCommentSearch',
      '-',
      // 'CKFinder',
      'customImageChoosingButton',
      // 'revisionHistory',
      'sourceEditing',
      'customCommentButton',
      'customButtonStructureTree',
      'customButtonPsCopy',
      'customButtonPsPaste',
      '|',
      'customCheckbox',
      '-',
      'heading',
      // 'headingProductions',
      // 'headingPS',
      'style',
      '|',
      'insertTemplate',
      //   "customButtonBlock",
      //   "customButtonLBlock",
      '|',
      'customButtonDecisionText',
      '|',
      'customCheckBoxReqSpec',
      // 'customButtonAddId',
      // "customButtonAddFinalParagraph",
      // "customButtonBlockWidget",
      '|',
      'CustomButtonPsInfo',
      'customButtonPsNote',
      'customButtonOriSearch',
      '|',
      'customButtonReference',
    ],
    shouldNotGroupWhenFull: true,
  },
  image: {
    styles: {
      // Defining custom styling options for the images.
      options: [
        {
          name: 'lined',
          icon: imageWithBorder,
          title: i18n.t('罫線有り'),
          className: 'bordered',
          modelElements: ['imageBlock'],
        },
        {
          name: 'noLined',
          icon: imageWithoutBorder,
          title: i18n.t('罫線無し'),
          className: 'noborder',
          modelElements: ['imageBlock'],
        },
        {
          name: 'fullColumn',
          icon: fullColumnImageWithBorder,
          title: i18n.t('全コラムイラスト罫線有り'),
          className: 'fullcolumn-bordered',
          modelElements: ['imageBlock'],
        },
        {
          name: 'fullColumnNoLined',
          icon: fullColumnImageWithoutBorder,
          title: i18n.t('全コラムイラスト罫線無し'),
          className: 'fullcolumn-noborder',
          modelElements: ['imageBlock'],
        },
        {
          name: 'inline',
          icon: inlineIcon,
        },
      ],
    },
    toolbar: [
      // 'comment',
      // 'imageTextAlternative',
      // 'toggleImageCaption',
      // '|',
      'imageStyle:inline',
      'imageStyle:lined',
      'imageStyle:noLined',
      'imageStyle:fullColumn',
      'imageStyle:fullColumnNoLined',
      // 'imageStyle:inline',
      // 'imageStyle:block',
      // 'imageStyle:side',
      '|',
      'customImageReplacingButton',
    ],
  },
  table: {
    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableCellProperties', 'tableProperties'],
    // tableToolbar: [
    // 	'comment'
    // ]
    tableProperties: {
      defaultProperties: {
        tableBorderWidth: '',
        tableBorderColor: '',
        tableBorderStyle: 'strip',
      },
    },
  },
  comments: {
    editorConfig: {
      // extraPlugins: [Bold, Italic, Autoformat, Link],
      i18n: i18n,
    },
    CommentView: MyCommentView,
    CommentThreadView: MyCommentThreadView,
  },
  htmlSupport: {
    allow: [
      {
        name: /.*/,
        attributes: true,
        classes: true,
        styles: true,
      },
    ],
  },
  ckfinder: {
    // Upload the images to the server using the CKFinder QuickUpload command.
    uploadUrl: '/ori/ori/ckfinder/core/connector/php/connector.php?command=QuickUpload&type=Images&responseType=json',
    // Define the CKFinder configuration (if necessary).
    options: {
      resourceType: 'contents',
      onInit(finder) {
        finder.on('files:choose', (evt) => {
          if (window.ckfinderMode === 'replace') {
            // 画像の差し替えをする場合
            const editor = window.editor;
            const model = editor.model;
            const selection = model.document.selection;
            const imageElement = selection.getSelectedElement();
            const newImageUrl = _.get(evt, 'data.files.models.0.changed.url');

            model.change((writer) => {
              writer.setAttribute('src', newImageUrl, imageElement);
            });
            evt.cancel(); // 差し替えのときは処理を止める
          }
        });
      },
    },
  },
  style: {
    definitions: [
      {
        name: i18n.t('タイプ別H'),
        element: 'span',
        classes: ['typehead'],
      },
      {
        name: i18n.t('タイプ別記付'),
        element: 'span',
        classes: ['OptionSymbol'],
      },
      {
        name: i18n.t('索引マーカー'),
        element: 'span',
        classes: ['indexterm'],
      },
      {
        name: 'hardkey',
        element: 'span',
        classes: ['hardkey'],
      },
      {
        name: 'softkey',
        element: 'span',
        classes: ['softkey'],
      },
      {
        name: 'voicecommand',
        element: 'span',
        classes: ['voicecommand'],
      },
      {
        name: 'Bluetooth',
        element: 'x-italic',
        classes: [''],
      },
      {
        name: 'TextItemSymbol',
        element: 'span',
        classes: ['TextItemSymbol'],
      },
      {
        name: i18n.t('レジストレーションマーク'),
        element: 'sup',
        // element: 'span',
        classes: ['reg'],
      },
      {
        name: 'Verbatim12',
        element: 'span',
        classes: ['Verbatim'],
      },
      {
        name: 'Verbatim14',
        element: 'span',
        classes: ['Verbatim14'],
      },
      {
        name: i18n.t('旧Bluetooth解除用'),
        element: 'span',
        classes: ['Bluetooth'],
      },
      // {
      //   name: 'custom_group',
      //   element: 'p',
      //   classes: ['custom_group'],
      // },
      // {
      //   name: 'custom_item',
      //   element: 'p',
      //   classes: ['custom_item'],
      // },
    ],
  },
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true,
    },
  },
  template: {
    definitions: [
      {
        title: i18n.t('イラスト有り本文'),
        description: i18n.t('イラスト有り本文'),
        data: `<section class="cbox-inner">
                <div class="left-wrap">
                    <figure class="image bordered">
                        <img src="/ori/s3/contents/common/other/dummy.png">
                    </figure>
                </div>
                <div class="right-wrap">
                    <p>
                        [right Contents]
                    </p>
                </div>
            </section>`,
        icon: undefined,
      },
      {
        title: i18n.t('警告/Warning'),
        description: i18n.t('警告/Warning'),
        data: `<section class="warning">
                <div class="one-wrap">
                    <p>
                        [Contents]
                    </p>
                </div>
            </section>`,
      },
      {
        title: i18n.t('危険/Danger'),
        description: i18n.t('危険/Danger'),
        data: `<section class="danger">
                <div class="one-wrap">
                    <p>
                        [Contents]
                    </p>
                </div>
            </section>`,
      },
      {
        title: i18n.t('注意/Caution'),
        description: i18n.t('注意/Caution'),
        data: `<section class="caution">
                <div class="one-wrap">
                    <p>
                        [Contents]
                    </p>
                </div>
            </section>`,
      },
      {
        title: i18n.t('イラスト有警告/Warning(image)'),
        description: i18n.t('イラスト有警告/Warning(image)'),
        data: `<section class="warning cbox-inner">
                <div class="left-wrap">
                    <figure class="image noborder">
                        <img src="/ori/s3/contents/common/other/dummy.png">
                    </figure>
                </div>
                <div class="right-wrap">
                    <p>
                        [Contents]
                    </p>
                </div>
            </section>`,
      },
      {
        title: i18n.t('イラスト有危険/Danger(image)'),
        description: i18n.t('イラスト有危険/Danger(image)'),
        data: `<section class="danger cbox-inner">
                <div class="left-wrap">
                    <figure class="image noborder">
                        <img src="/ori/s3/contents/common/other/dummy.png">
                    </figure>
                </div>
                <div class="right-wrap">
                    <p>
                        [Contents]
                    </p>
                </div>
            </section>`,
      },
      {
        title: i18n.t('イラスト有注意/Caution(image)'),
        description: i18n.t('イラスト有注意/Caution(image)'),
        data: `<section class="caution cbox-inner">
                <div class="left-wrap">
                    <figure class="image noborder">
                        <img src="/ori/s3/contents/common/other/dummy.png">
                    </figure>
                </div>
                <div class="right-wrap">
                    <p>
                        [Contents]
                    </p>
                </div>
            </section>`,
      },
      {
        title: i18n.t('指示事項/Supplement'),
        description: i18n.t('指示事項/Supplement'),
        data: `<section class="supplement">
                <div class="one-wrap">
                    <p class="supplement_subhead">
                        [Directive text]
                    </p>
                    <p class="supplement_para">
                        [Directive Note text]
                    </p>
                </div>
            </section>`,
      },
      {
        title: i18n.t('L3構造'),
        description: i18n.t('L3構造'),
        data: () => {
          function getUniqueId() {
            const dt = new Date();
            return (
              '#' +
              ('00' + dt.getHours()).slice(-2) +
              ('00' + (dt.getMinutes() + 1)).slice(-2) +
              ('00' + dt.getSeconds()).slice(-2) +
              ('000' + dt.getMilliseconds()).slice(-3)
            );
          }
          return `<section class="l3"><div class="l3"><div section_id="${getUniqueId()}"><div class="title" ps_no="" ps_version_id="" ps_derivation_id="" confirmed="2"><h3 id="${getUniqueId()}h">[Title]</h3></div><div class="contents"><p>[Contents]</p></div></div></div></section>`;
        },
      },
      {
        title: i18n.t('L4構造'),
        description: i18n.t('L4構造'),
        data: () => {
          function getUniqueId() {
            const dt = new Date();
            return (
              '#' +
              ('00' + dt.getHours()).slice(-2) +
              ('00' + (dt.getMinutes() + 1)).slice(-2) +
              ('00' + dt.getSeconds()).slice(-2) +
              ('000' + dt.getMilliseconds()).slice(-3)
            );
          }
          // return `<section class="l4"><div class="l4" section_id="${getUniqueId()}" id=""><div class="title" id="" ps_no="" ps_version_id="" ps_derivation_id="" fsr_id="" confirmed="0"><h4 id="${getUniqueId()}h">[Title]</h4></div><div class="contents" id=""><p>[Contents]</p></div></div></section>`
          // return `<section class="l4"><div class="l4"><div section_id="${getUniqueId()}"><div class="title" ps_no="" ps_version_id="" ps_derivation_id="" fsr_id="" confirmed="0"><h4 id="${getUniqueId()}h">[Title]</h4></div><div class="contents"><p>[Contents]</p></div></div></div></section>`;
          return `<section class="l4"><div class="l4"><div section_id="${getUniqueId()}"><div class="title" ps_no="" ps_version_id="" ps_derivation_id="" confirmed="2"><h4 id="${getUniqueId()}h">[Title]</h4></div><div class="contents"><p>[Contents]</p></div></div></div></section>`;
        },
      },
      {
        title: i18n.t('L5構造'),
        description: i18n.t('L5構造'),
        data: () => {
          function getUniqueId() {
            const dt = new Date();
            return (
              '#' +
              ('00' + dt.getHours()).slice(-2) +
              ('00' + (dt.getMinutes() + 1)).slice(-2) +
              ('00' + dt.getSeconds()).slice(-2) +
              ('000' + dt.getMilliseconds()).slice(-3)
            );
          }
          // return `<section class="l5"><div class="l5"><div section_id="${getUniqueId()}"><div class="title" ps_no="" ps_version_id="" ps_derivation_id="" fsr_id="" confirmed="0"><h5 id="${getUniqueId()}h">[Title]</h5></div><div class="contents"><p>[Contents]</p></div></div></div></section>`;
          return `<section class="l5"><div class="l5"><div section_id="${getUniqueId()}"><div class="title" ps_no="" ps_version_id="" ps_derivation_id="" confirmed="2"><h5 id="${getUniqueId()}h">[Title]</h5></div><div class="contents"><p>[Contents]</p></div></div></div></section>`;
        },
      },
      /*
      * ORIAS-254により除去
      {
        title: i18n.t('セパレータ'),
        description: i18n.t('セパレータ'),
        data: '<p class="Separator">---------------------------------------------------------------------------------------------------------------</p>',
      },
      */
    ],
  },
};

// 見出し設定
const sectionHeadingConfig = {
  heading: {
    options: [
      {
        model: 'heading1',
        view: 'h1',
        title: i18n.t('L1見出し'),
        class: 'ck-heading_heading1',
      },
      {
        model: 'heading2',
        view: 'h2',
        title: i18n.t('L2見出し'),
        class: 'ck-heading_heading2',
      },
      {
        model: 'heading3',
        view: 'h3',
        title: i18n.t('L3見出し'),
        class: 'ck-heading_heading3',
      },
      {
        model: 'heading4',
        view: 'h4',
        title: i18n.t('L4見出し'),
        class: 'ck-heading_heading4',
      },
      {
        model: 'heading5',
        view: 'h5',
        title: i18n.t('小見出し'),
        class: 'ck-heading_heading5',
      },
      {
        model: 'paragraph',
        title: i18n.t('本文'),
        class: 'ck-heading_paragraph',
      },
      {
        model: 'paragraphSupplementSubhead',
        view: {
          name: 'p',
          classes: 'supplement_subhead',
        },
        title: i18n.t('指示主文'),
        class: 'ck-paragraph_supplement_subhead',
        converterPriority: 'high',
      },
      {
        model: 'paragraphSupplementPara',
        view: {
          name: 'p',
          classes: 'supplement_para',
        },
        title: i18n.t('指示補足文'),
        class: 'ck-paragraph_supplement_para',
        converterPriority: 'high',
      },
      {
        model: 'paragraphReferGear',
        view: {
          name: 'p',
          classes: ['refer', 'gear'],
        },
        title: i18n.t('車両設定マーク'),
        class: 'ck-paragraph_refer_gear',
        converterPriority: 'high',
      },
      {
        model: 'paragraphRefer1',
        view: {
          name: 'p',
          classes: 'refer',
        },
        title: i18n.t('参照ページ'),
        class: 'ck-paragraph_refer',
        converterPriority: 'high',
      },
      {
        model: 'warningListTitle',
        view: {
          name: 'p',
          classes: 'warning_list_title',
        },
        title: 'warning_list_title',
        class: 'ck-warning-list-title',
        converterPriority: 'high',
      },
      {
        model: 'illustHeadline',
        view: {
          name: 'p',
          classes: 'imgtitle',
        },
        title: i18n.t('イラスト見出し'),
        class: 'ck-imgtitle',
        converterPriority: 'high',
      },
      {
        model: 'customGroup',
        view: {
          name: 'p',
          classes: 'custom_group',
        },
        title: 'custom_group',
        class: 'ck-custom-group',
        converterPriority: 'high',
      },
      {
        model: 'customItem',
        view: {
          name: 'p',
          classes: 'custom_item',
        },
        title: 'custom_item',
        class: 'ck-custom-item',
        converterPriority: 'high',
      },
    ],
  },
};
const psHeadingConfig = {
  heading: {
    options: [
      {
        model: 'heading3',
        view: 'h3',
        title: i18n.t('L1〜L５見出し'),
        class: 'ck-heading_heading3',
      },
      {
        model: 'heading5',
        view: 'h5',
        title: i18n.t('小見出し'),
        class: 'ck-heading_heading5',
      },
      {
        model: 'paragraph',
        title: i18n.t('本文'),
        class: 'ck-heading_paragraph',
      },
      {
        model: 'paragraphSupplementSubhead',
        view: {
          name: 'p',
          classes: 'supplement_subhead',
        },
        title: i18n.t('指示主文'),
        class: 'ck-paragraph_supplement_subhead',
        converterPriority: 'high',
      },
      {
        model: 'paragraphSupplementPara',
        view: {
          name: 'p',
          classes: 'supplement_para',
        },
        title: i18n.t('指示補足文'),
        class: 'ck-paragraph_supplement_para',
        converterPriority: 'high',
      },
      {
        model: 'paragraphReferGear',
        view: {
          name: 'p',
          classes: ['refer', 'gear'],
        },
        title: i18n.t('車両設定マーク'),
        class: 'ck-paragraph_refer_gear',
        converterPriority: 'high',
      },
      {
        model: 'paragraphRefer1',
        view: {
          name: 'p',
          classes: 'refer',
        },
        title: i18n.t('参照ページ'),
        class: 'ck-paragraph_refer',
        converterPriority: 'high',
      },
      {
        model: 'warningListTitle',
        view: {
          name: 'p',
          classes: 'warning_list_title',
        },
        title: 'warning_list_title',
        class: 'ck-warning-list-title',
        converterPriority: 'high',
      },
      {
        model: 'illustHeadline',
        view: {
          name: 'p',
          classes: 'imgtitle',
        },
        title: i18n.t('イラスト見出し'),
        class: 'ck-imgtitle',
        converterPriority: 'high',
      },
      {
        model: 'customGroup',
        view: {
          name: 'p',
          classes: 'custom_group',
        },
        title: 'custom_group',
        class: 'ck-custom-group',
        converterPriority: 'high',
      },
      {
        model: 'customItem',
        view: {
          name: 'p',
          classes: 'custom_item',
        },
        title: 'custom_item',
        class: 'ck-custom-item',
        converterPriority: 'high',
      },
    ],
  },
};

class PsEditor extends ClassicEditor {}
class SectionEditor extends ClassicEditor {}

// プラグイン設定
PsEditor.builtinPlugins = psBuiltinPlugins;
SectionEditor.builtinPlugins = sectionBuiltinPlugins;

// コンフィグ設定
PsEditor.defaultConfig = {
  ...baseConfig,
  ...psHeadingConfig,
};
SectionEditor.defaultConfig = {
  ...baseConfig,
  ...sectionHeadingConfig,
};

export default {
  PsEditor,
  SectionEditor,
};
